/**
 * OverageApprovalCard — owner-only toggle inside Plan & Billing that
 * tells the invite endpoint to refuse with HTTP 402
 * `overage_approval_required` whenever a new buyer would push the
 * seller beyond their plan's included count. Default OFF — most
 * sellers want frictionless growth; this is for the regulated /
 * paranoid power user who wants a hard gate.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Switch } from "./ui/switch";
import { ShieldAlert, Loader2 } from "lucide-react";


export default function OverageApprovalCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [on, setOn]           = useState(false);

  useEffect(() => {
    api.get("/billing/status")
       .then(r => setOn(!!r.data.require_overage_approval))
       .catch(() => {})
       .finally(() => setLoading(false));
  }, []);

  const toggle = async (next) => {
    setSaving(true);
    setOn(next); // optimistic
    try {
      await api.post("/billing/preferences", { require_overage_approval: next });
      toast.success(next
        ? "Approval required for buyers beyond your included count"
        : "Buyers will be added silently — billed on next cycle");
    } catch (e) {
      setOn(!next);
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border bg-background p-5 flex items-center gap-2 text-foreground/40 text-sm">
        <Loader2 className="w-4 h-4 animate-spin"/> Loading preferences…
      </div>
    );
  }

  return (
    <div className="rounded-2xl border bg-background p-5 flex items-start gap-3" data-testid="overage-approval-card">
      <div className="w-9 h-9 rounded-full bg-foreground/5 text-foreground/70 grid place-items-center shrink-0">
        <ShieldAlert className="w-4 h-4"/>
      </div>
      <div className="flex-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="display text-lg tracking-tight">Require my approval before adding overage buyers</div>
            <p className="text-[12.5px] text-foreground/55 mt-0.5 max-w-xl">
              When ON, inviting a buyer that pushes you past your plan&apos;s included count first
              opens a confirm dialog. When OFF (default), buyers are added silently and the
              per-buyer fee shows on your next monthly bill.
            </p>
          </div>
          <Switch checked={on} onCheckedChange={toggle} disabled={saving} data-testid="overage-approval-toggle"/>
        </div>
      </div>
    </div>
  );
}
